/**
 * Central API route registry — the SINGLE source of every NestJS endpoint path.
 *
 * Mirrors Himam's `apiHandler` (`path:` functions per domain). No route string
 * literal may live anywhere else in the frontend; repositories resolve their
 * paths from here so a backend endpoint change is a one-line edit in this file.
 *
 * During the Supabase phase a repository may still call `supabase.from('<table>')`
 * directly (the table name stays centralized inside that one repository file).
 * When a slice flips to NestJS, the repository swaps to the Nest client
 * (`src/services/api.ts`) using the paths below. End state: every path lives here.
 *
 * See: docs/manifests/manifest-6-supabase-to-nestjs.md
 *      dallali-notes/permissions-migration-handoff.md
 */
export const API_ROUTES = {
  serviceRequests: {
    // `companyId` (super_admin only) scopes the cross-company read to a single
    // company — the dashboard's company tab. The guarded endpoint ignores it for
    // every other role (they are pinned to their JWT company).
    list: (companyId?: string) =>
      companyId
        ? `/service-requests?companyId=${encodeURIComponent(companyId)}`
        : "/service-requests",
    getById: (id: string) => `/service-requests/${encodeURIComponent(id)}`,
    create: () => "/service-requests",
    update: (id: string) => `/service-requests/${encodeURIComponent(id)}`,
    remove: (id: string) => `/service-requests/${encodeURIComponent(id)}`,
    stats: (companyId?: string, startDate?: string, endDate?: string) => {
      const params = new URLSearchParams();
      if (companyId) params.set("companyId", companyId);
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      const qs = params.toString();
      return qs ? `/service-requests/stats?${qs}` : "/service-requests/stats";
    },
    // Companies that have requests (super_admin tab strip; main company first).
    companies: () => "/service-requests/companies",
    // Owner-portal self-service read — scoped to the caller's own phone.
    ownerRequests: () => "/service-requests/owner-requests",
    // Owner-portal self-service create — auth-only, no PermissionGuard.
    ownerCreate: () => "/service-requests/owner-requests",

    // ── Master Card per-unit read model (slice-flip S1) ──────────────────────
    // Staff card detail (v_service_request_detail) — service_requests.view.
    cardDetail: (id: string) =>
      `/service-requests/${encodeURIComponent(id)}/detail`,
    // Owner card detail — auth-only, scoped to the caller's owner ids server-side.
    ownerCardDetail: (id: string) =>
      `/service-requests/owner-requests/${encodeURIComponent(id)}/detail`,
    // Admin Master Card board — cards that have units. service_requests.view.
    // `companyId` (super_admin only) narrows to the dashboard's company tab; the
    // guarded endpoint ignores it for every other role (pinned to their JWT company).
    masterCards: (companyId?: string) =>
      companyId
        ? `/service-requests/master-cards?companyId=${encodeURIComponent(companyId)}`
        : "/service-requests/master-cards",
    // Per-card pipeline context for the task board (bounded id set → query param).
    masterCardTaskContext: (ids: string[]) =>
      `/service-requests/master-cards/task-context?ids=${encodeURIComponent(ids.join(","))}`,
    // The inspection steps an employee must fill for a task (assignee-or-staff).
    taskInspectionSteps: (taskId: string) =>
      `/service-requests/tasks/${encodeURIComponent(taskId)}/inspection-steps`,
    // The maintenance/cleaning actions an employee must complete for a task.
    taskActionCompletions: (taskId: string) =>
      `/service-requests/tasks/${encodeURIComponent(taskId)}/action-completions`,
    // Visits stage · employee report + admin review.
    taskVisitSteps: (taskId: string) =>
      `/service-requests/tasks/${encodeURIComponent(taskId)}/visit-steps`,
    submitVisitResult: (unitId: string) =>
      `/service-requests/units/${encodeURIComponent(unitId)}/visit-result`,
    reviewVisit: (unitId: string) =>
      `/service-requests/units/${encodeURIComponent(unitId)}/review-visit`,
    // Contracting stage (F9) · employee draft + owner review.
    taskContractSteps: (taskId: string) =>
      `/service-requests/tasks/${encodeURIComponent(taskId)}/contract-steps`,
    servicePrices: () => `/service-requests/service-prices`,
    submitContractDraft: (unitId: string) =>
      `/service-requests/units/${encodeURIComponent(unitId)}/contract-draft`,
    unitContractDrafts: (unitId: string) =>
      `/service-requests/units/${encodeURIComponent(unitId)}/contract-drafts`,
    reviewContractDraft: (unitId: string) =>
      `/service-requests/units/${encodeURIComponent(unitId)}/review-contract-draft`,
    activateContractDraft: (unitId: string) =>
      `/service-requests/units/${encodeURIComponent(unitId)}/activate-contract-draft`,
    // Card-scoped assign pickers — the CARD's company, not the caller's active one.
    assignableEmployees: (companyId: string) =>
      `/service-requests/companies/${encodeURIComponent(companyId)}/assignable-employees`,
    assignableProviders: (companyId: string) =>
      `/service-requests/companies/${encodeURIComponent(companyId)}/assignable-providers`,

    // ── Master Card writes (slice-flip S2 — assignee/employee) ───────────────
    submitInspectionResult: (unitId: string) =>
      `/service-requests/units/${encodeURIComponent(unitId)}/inspection-result`,
    submitActionCompletion: (actionId: string) =>
      `/service-requests/actions/${encodeURIComponent(actionId)}/completion`,
    completeCardTask: (taskId: string) =>
      `/service-requests/tasks/${encodeURIComponent(taskId)}/complete`,
    reportNote: (id: string) => `/service-requests/${encodeURIComponent(id)}/notes`,
    availableUnits: (propertyId: string, serviceType: string) =>
      `/service-requests/available-units?propertyId=${encodeURIComponent(propertyId)}&serviceType=${encodeURIComponent(serviceType)}`,

    // ── Master Card writes (slice-flip S3 — admin lifecycle) ─────────────────
    reviewInspection: (unitId: string) =>
      `/service-requests/units/${encodeURIComponent(unitId)}/review-inspection`,
    assignUnitStep: (unitId: string) =>
      `/service-requests/units/${encodeURIComponent(unitId)}/assign`,
    assignCardUnits: (cardId: string) =>
      `/service-requests/${encodeURIComponent(cardId)}/assign`,
    reviewCardInspection: (cardId: string) =>
      `/service-requests/${encodeURIComponent(cardId)}/review-inspection`,
    cardContract: (cardId: string) =>
      `/service-requests/${encodeURIComponent(cardId)}/contract`,
    unitContract: (unitId: string) =>
      `/service-requests/units/${encodeURIComponent(unitId)}/contract`,
    payServiceFees: (cardId: string) =>
      `/service-requests/${encodeURIComponent(cardId)}/pay-service-fees`,
    reviewActionQuote: (actionId: string) =>
      `/service-requests/actions/${encodeURIComponent(actionId)}/review-quote`,
    adminResolveRejection: (actionId: string) =>
      `/service-requests/actions/${encodeURIComponent(actionId)}/resolve-rejection`,
    approveActionCompletion: (actionId: string) =>
      `/service-requests/actions/${encodeURIComponent(actionId)}/approve-completion`,
    setCleaningPrice: (actionId: string) =>
      `/service-requests/actions/${encodeURIComponent(actionId)}/cleaning-price`,
    convertNoteToAction: (noteId: string) =>
      `/service-requests/notes/${encodeURIComponent(noteId)}/convert`,
    dismissNote: (noteId: string) =>
      `/service-requests/notes/${encodeURIComponent(noteId)}/dismiss`,
    assignActionProvider: (actionId: string) =>
      `/service-requests/actions/${encodeURIComponent(actionId)}/assign-provider`,

    // ── Master Card writes (slice-flip S4 — owner / financial / provider) ────
    createCard: () => "/service-requests/owner-requests/cards",
    ownerDecideAction: (actionId: string) =>
      `/service-requests/owner-requests/actions/${encodeURIComponent(actionId)}/decide`,
    overrideActionPayment: (actionId: string) =>
      `/service-requests/actions/${encodeURIComponent(actionId)}/override-payment`,
    submitActionQuote: (actionId: string) =>
      `/service-requests/actions/${encodeURIComponent(actionId)}/quote`,
    actionQuoteDetails: (actionId: string) =>
      `/service-requests/actions/${encodeURIComponent(actionId)}/quote-details`,
    providerActions: () => "/service-requests/provider-actions",

    // ── F1 · per-unit documents ──────────────────────────────────────────────
    unitDocuments: (unitId: string) =>
      `/service-requests/units/${encodeURIComponent(unitId)}/documents`,
    cardDocuments: (id: string) =>
      `/service-requests/${encodeURIComponent(id)}/documents`,

    // ── F5 · return-to-owner + owner edit-units ──────────────────────────────
    returnToOwner: (id: string) =>
      `/service-requests/${encodeURIComponent(id)}/return-to-owner`,
    ownerAddUnits: (id: string) =>
      `/service-requests/owner-requests/${encodeURIComponent(id)}/units`,
    ownerRemoveUnit: (id: string, unitId: string) =>
      `/service-requests/owner-requests/${encodeURIComponent(id)}/units/${encodeURIComponent(unitId)}`,
  },
  receipts: {
    list: () => "/receipts",
    getById: (id: string) => `/receipts/${encodeURIComponent(id)}`,
    create: () => "/receipts",
    delete: (id: string) => `/receipts/${encodeURIComponent(id)}`,
  },
  // Great Migration slice a14 (N-3) — companies control-plane, served by the
  // guarded endpoints in apps/api/src/companies/*. `main` is the
  // platform-global main-company id (companies.is_main) — not tenant-scoped.
  // `current` is the caller's OWN company row (companyId token-resolved
  // server-side — never sent). The super-admin trio wraps the cross-tenant
  // control-plane RPCs (JwtSuperAdminGuard; user-token client server-side).
  companies: {
    main: () => "/companies/main",
    current: () => "/companies/current",
    superAdminList: () => "/companies/super-admin/list",
    setMain: () => "/companies/super-admin/main",
    setActive: (id: string) =>
      `/companies/super-admin/${encodeURIComponent(id)}/active`,
  },
  // Profiles are USER-keyed (id = auth user id) — `me` reads/writes the
  // caller's own row from the JWT; `emailTaken` is the auth-only cross-tenant
  // uniqueness probe (boolean only); `superAdminLookup` is the
  // JwtSuperAdminGuard-gated Grant-Super-Admin lookup (exactly one of
  // userId | phone; phone variants expanded server-side).
  /** The caller's own KYC profile (docs/specs/kyc-aml-profile.md, "Wizard API"). */
  kyc: {
    me: () => "/kyc/me",
    draft: () => "/kyc/me/draft",
    documents: () => "/kyc/me/documents",
    payoutAccount: () => "/kyc/me/payout-account",
    submit: () => "/kyc/me/submit",
  },
  /** The compliance officer's KYC queue and cases (spec "Compliance API"; explicit grant only). */
  kycCompliance: {
    access: () => "/compliance/kyc/access",
    queue: () => "/compliance/kyc/queue",
    case: (profileId: string) => `/compliance/kyc/cases/${encodeURIComponent(profileId)}`,
    screenings: (profileId: string) => `/compliance/kyc/cases/${encodeURIComponent(profileId)}/screenings`,
    decisions: (profileId: string) => `/compliance/kyc/cases/${encodeURIComponent(profileId)}/decisions`,
  },
  profiles: {
    me: () => "/profiles/me",
    emailTaken: (email: string) =>
      `/profiles/email-taken?email=${encodeURIComponent(email)}`,
    superAdminLookup: (params: { userId?: string; phone?: string }) => {
      const qs = new URLSearchParams();
      if (params.userId) qs.set("userId", params.userId);
      if (params.phone) qs.set("phone", params.phone);
      return `/profiles/super-admin/lookup?${qs.toString()}`;
    },
  },
  nafathVerifications: {
    /**
     * GET /nafath/owner-identity?nationalId=&userId=
     *
     * Returns the most-recent COMPLETED Nafath identity for an owner.
     * company_id is NEVER a query param — the server reads it from the JWT.
     * Guards: SupabaseAuthGuard + PermissionGuard + owners.view.
     */
    ownerIdentity: (nationalId: string, userId?: string | null) => {
      const params = new URLSearchParams({ nationalId });
      if (userId) params.set("userId", userId);
      return `/nafath/owner-identity?${params.toString()}`;
    },
  },
  properties: {
    list: () => "/properties",
    cards: () => "/properties/cards",
    getById: (id: string) => `/properties/${encodeURIComponent(id)}`,
    create: () => "/properties",
    update: (id: string) => `/properties/${encodeURIComponent(id)}`,
    remove: (id: string) => `/properties/${encodeURIComponent(id)}`,
    stats: (startDate?: string, endDate?: string) => {
      const params = new URLSearchParams();
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      const qs = params.toString();
      return qs ? `/properties/stats?${qs}` : "/properties/stats";
    },
    translate: (id: string) => `/properties/${encodeURIComponent(id)}/translate`,
  },
  units: {
    list: () => "/units",
    getById: (id: string) => `/units/${encodeURIComponent(id)}`,
    create: () => "/units",
    update: (id: string) => `/units/${encodeURIComponent(id)}`,
    remove: (id: string) => `/units/${encodeURIComponent(id)}`,
    stats: (startDate?: string, endDate?: string) => {
      const params = new URLSearchParams();
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      const qs = params.toString();
      return qs ? `/units/stats?${qs}` : "/units/stats";
    },
    translate: (id: string) => `/units/${encodeURIComponent(id)}/translate`,
  },
  // Leasing desk — Nest-only slice (apps/api/src/leasing/leasing.controller.ts).
  leasing: {
    availability: (propertyId: string) =>
      `/leasing/properties/${encodeURIComponent(propertyId)}/availability`,
    desk: (propertyId: string) => `/leasing/properties/${encodeURIComponent(propertyId)}/desk`,
    settings: (propertyId: string) => `/leasing/properties/${encodeURIComponent(propertyId)}/settings`,
    holds: (propertyId: string) => `/leasing/properties/${encodeURIComponent(propertyId)}/holds`,
    holdAction: (id: string, action: "extend" | "release") => `/leasing/holds/${encodeURIComponent(id)}/${action}`,
    quotations: (propertyId: string) => `/leasing/properties/${encodeURIComponent(propertyId)}/quotations`,
    quotationPreview: (propertyId: string) =>
      `/leasing/properties/${encodeURIComponent(propertyId)}/quotations/preview`,
    quotation: (id: string) => `/leasing/quotations/${encodeURIComponent(id)}`,
    quotationAction: (id: string, action: "revise" | "approve" | "reject" | "withdraw") =>
      `/leasing/quotations/${encodeURIComponent(id)}/${action}`,
    publicQuotationView: () => "/public/leasing/quotation-view",
    publicQuotationAccept: () => "/public/leasing/quotation-accept",
    publicQuotationPdf: (token: string) => `/public/leasing/quotations/${encodeURIComponent(token)}/pdf`,
  },
  // Media capture (structured unit/building photo shoot) — Nest-only slice
  // (apps/api/src/media-capture/media-capture.controller.ts).
  mediaCapture: {
    unitWorkspace: (unitId: string) => `/media-capture/units/${encodeURIComponent(unitId)}`,
    propertyWorkspace: (propertyId: string) =>
      `/media-capture/properties/${encodeURIComponent(propertyId)}`,
    startSession: () => "/media-capture/sessions",
    updateSession: (id: string) => `/media-capture/sessions/${encodeURIComponent(id)}`,
    importLegacy: () => "/media-capture/items/import-legacy",
    updateItem: (id: string) => `/media-capture/items/${encodeURIComponent(id)}`,
    deleteItem: (id: string) => `/media-capture/items/${encodeURIComponent(id)}`,
    originalUrl: (id: string) => `/media-capture/items/${encodeURIComponent(id)}/original-url`,
    reviewOriginalUrl: (id: string) =>
      `/media-capture/review/items/${encodeURIComponent(id)}/original-url`,
    createUpload: () => "/media-capture/uploads",
    uploadStatus: (itemId: string) => `/media-capture/uploads/${encodeURIComponent(itemId)}`,
    uploadParts: (itemId: string) =>
      `/media-capture/uploads/${encodeURIComponent(itemId)}/parts`,
    completeUpload: (itemId: string) =>
      `/media-capture/uploads/${encodeURIComponent(itemId)}/complete`,
    abortUpload: (itemId: string) => `/media-capture/uploads/${encodeURIComponent(itemId)}`,
    // Review workflow (PR 4a API / 4b UI)
    submitSession: (id: string) => `/media-capture/sessions/${encodeURIComponent(id)}/submit`,
    reviewQueue: () => "/media-capture/review-queue",
    sessionReview: (id: string) => `/media-capture/sessions/${encodeURIComponent(id)}/review`,
    reviewItem: (id: string) => `/media-capture/items/${encodeURIComponent(id)}/review`,
    sessionDecision: (id: string) => `/media-capture/sessions/${encodeURIComponent(id)}/decision`,
  },
  // Owner self-service reads (OwnerPortalGuard: x-active-role: owner).
  ownerPortal: {
    property: (id: string) => `/owner-portal/properties/${encodeURIComponent(id)}`,
  },
  contracts: {
    list: () => "/contracts",
    getById: (id: string) => `/contracts/${encodeURIComponent(id)}`,
    create: () => "/contracts",
    createWithInvoices: () => "/contracts/with-invoices",
    update: (id: string) => `/contracts/${encodeURIComponent(id)}`,
    updateWithInvoices: (id: string) => `/contracts/${encodeURIComponent(id)}/with-invoices`,
    // Cascade soft-delete (delete_contract_cascade RPC wrap) — company scope
    // derived server-side from the JWT, guarded by contracts.delete + audited.
    remove: (id: string) => `/contracts/${encodeURIComponent(id)}`,
    // Revert all units on a terminated/void contract to available
    // (revert_units_for_terminated_contract RPC wrap) — contracts.edit + audited.
    revertUnits: (id: string) => `/contracts/${encodeURIComponent(id)}/revert-units`,
    // Next per-company contract number (generate_contract_number RPC wrap) for
    // the simple-create flow — guarded by contracts.create.
    nextNumber: () => "/contracts/next-number",
    // SELF read (S9): the caller's OWN contracts (auth-only, no contracts.view —
    // the server resolves the owner person-wide from the JWT).
    owner: () => "/contracts/owner",
    byTenant: (tenantId: string) => `/contracts?tenant_id=${tenantId}`,
    byUnit: (unitId: string) => `/contracts?unit_id=${unitId}`,
    stats: (startDate?: string, endDate?: string) => {
      const params = new URLSearchParams();
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      const qs = params.toString();
      return qs ? `/contracts/stats?${qs}` : "/contracts/stats";
    },
  },
  tenants: {
    list: () => "/tenants",
    getById: (id: string) => `/tenants/${encodeURIComponent(id)}`,
    create: () => "/tenants",
    update: (id: string) => `/tenants/${encodeURIComponent(id)}`,
    remove: (id: string) => `/tenants/${encodeURIComponent(id)}`,
    deactivate: (id: string) => `/tenants/${encodeURIComponent(id)}/deactivate`,
    stats: () => "/tenants/stats",
    statsEnhanced: () => "/tenants/stats-enhanced",
  },
  owners: {
    getById: (id: string) => `/owners/${encodeURIComponent(id)}`,
    getByIdWithStatus: (id: string) => `/owners/${encodeURIComponent(id)}/status`,
    create: () => "/owners",
    update: (id: string) => `/owners/${encodeURIComponent(id)}`,
    deactivate: (id: string) => `/owners/${encodeURIComponent(id)}/deactivate`,
  },
  users: {
    me: () => "/users/me",
    getById: (id: string) => `/users/${encodeURIComponent(id)}`,
    // POST — the id set (up to 500 list rows) exceeds URL limits.
    identities: () => "/users/identities",
  },
  invoices: {
    list: () => "/invoices",
    getById: (id: string) => `/invoices/${encodeURIComponent(id)}`,
    create: () => "/invoices",
    update: (id: string) => `/invoices/${encodeURIComponent(id)}`,
    listByContract: (contractId: string) => `/invoices?contract_id=${contractId}`,
    listOpen: () => "/invoices/open",
    stats: () => "/invoices/stats",
    accountingSyncState: () => "/invoices/accounting-sync-state",
    notifications: (invoiceId: string) => `/invoices/${encodeURIComponent(invoiceId)}/notifications`,
    masterCreate: () => "/invoices/master",
    masterPay: (id: string) => `/invoices/master/${encodeURIComponent(id)}/pay`,
    masterCancel: (id: string) => `/invoices/master/${encodeURIComponent(id)}/cancel`,
    masterDelete: (id: string) => `/invoices/master/${encodeURIComponent(id)}`,
    verifyPaymentProof: (id: string) =>
      `/invoices/payment-proofs/${encodeURIComponent(id)}/verify`,
    // Tenant-portal proof SUBMIT lives on the finance controller (tenant
    // self-service, auth-only) — grouped here with its staff-side sibling
    // `verifyPaymentProof` because both are invoicesRepository methods.
    submitPaymentProof: (id: string) =>
      `/finance/payment-proofs/${encodeURIComponent(id)}/submit`,
    // Super-admin platform KPI (SuperAdminGuard) — platform-wide sum for one status.
    adminSumByStatus: (status: string) =>
      `/invoices/admin/sum-by-status?status=${encodeURIComponent(status)}`,
  },
  expenses: {
    list: () => "/expenses",
    getById: (id: string) => `/expenses/${encodeURIComponent(id)}`,
    create: () => "/expenses",
    update: (id: string) => `/expenses/${encodeURIComponent(id)}`,
    remove: (id: string) => `/expenses/${encodeURIComponent(id)}`,
    generateInvoice: (id: string) => `/expenses/${encodeURIComponent(id)}/generate-invoice`,
    stats: () => "/expenses/stats",
    summary: () => "/expenses/summary",
    monthly: () => "/expenses/monthly",
    financial: () => "/expenses/financial",
    totals: () => "/expenses/totals",
    recent: () => "/expenses/recent",
    incomeStatement: () => "/expenses/income-statement",
    propertyPerformance: () => "/expenses/property-performance",
    operatingExpensesReport: () => "/expenses/operating-expenses-report",
    byProperty: (propertyId: string) => `/expenses/by-property/${encodeURIComponent(propertyId)}`,
    ownerFinancials: (startDate?: string, endDate?: string) => {
      const params = new URLSearchParams();
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      const qs = params.toString();
      return qs ? `/expenses/owner-financials?${qs}` : "/expenses/owner-financials";
    },
    ownerDocuments: () => "/expenses/owner-documents",
    ownerPayouts: () => "/expenses/owner-payouts",
    ownerView: (ownerId: string) => `/expenses/owner-view/${encodeURIComponent(ownerId)}`,
  },
  ownerPayouts: {
    list: () => "/owner-payouts",
    create: () => "/owner-payouts",
  },
  payoutSettlements: {
    // Month-end "mark as settled" log for the Field-Agent Payouts report.
    // Pass the active companyId so a super_admin is scoped to the dashboard's
    // company (a non-super-admin caller is pinned to their JWT company and the
    // param is ignored for them) — mirrors employees.list / serviceRequests.list.
    list: (companyId?: string) =>
      companyId
        ? `/payout-settlements?companyId=${encodeURIComponent(companyId)}`
        : "/payout-settlements",
    create: () => "/payout-settlements",
  },
  // Field-agent payouts (items 6/7): staff bank accounts + report money columns.
  fieldAgentPayouts: {
    // AUTHED any staff member: read / set MY OWN bank account (SA IBAN).
    myBankAccount: () => "/field-agent-payouts/me/bank-account",
    // finance.view: full bank details of every staff member (payout export).
    bankDetails: () => "/field-agent-payouts/bank-details",
    // finance.view: net referral commission per agent for a month (YYYY-MM) or "all".
    referralCommissions: (period: string) =>
      `/field-agent-payouts/referral-commissions?period=${encodeURIComponent(period)}`,
  },
  messages: {
    list: () => "/messages",
    getById: (id: string) => `/messages/${encodeURIComponent(id)}`,
    create: () => "/messages",
    markRead: (id: string) => `/messages/${encodeURIComponent(id)}/read`,
  },
  listings: {
    // ── PUBLIC (unauthenticated marketplace/SEO surface — NO guard) ───────────
    publicList: () => "/listings/public",
    publicGetById: (id: string) => `/listings/public/${encodeURIComponent(id)}`,
    publicCities: () => "/listings/public/cities",
    publicByProperty: (propertyId: string) =>
      `/listings/public/by-property/${encodeURIComponent(propertyId)}`,
    publicProperty: (id: string) => `/listings/public/property/${encodeURIComponent(id)}`,
    incrementView: (id: string) => `/listings/public/${encodeURIComponent(id)}/view`,
    ratingRequestByToken: (token: string) =>
      `/listings/public/rating-request/${encodeURIComponent(token)}`,
    submitPropertyRequest: () => "/listings/public/property-requests",
    // ── STAFF (advertisements.* guarded management surface) ───────────────────
    list: () => "/listings",
    getById: (id: string) => `/listings/${encodeURIComponent(id)}`,
    create: () => "/listings",
    update: (id: string) => `/listings/${encodeURIComponent(id)}`,
    translate: (id: string) => `/listings/${encodeURIComponent(id)}/translate`,
    byUnit: (unitId: string) => `/listings?unit_id=${unitId}`,
    byProperty: (propertyId: string) =>
      `/listings?property_id=${propertyId}`,
    stats: (startDate?: string, endDate?: string) => {
      const params = new URLSearchParams();
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      const qs = params.toString();
      return qs ? `/listings/stats?${qs}` : "/listings/stats";
    },
    propertyRequestsStats: () => "/listings/property-requests/stats",
  },
  projects: {
    // ── PUBLIC (unauthenticated off-plan projects gallery — NO guard) ─────────
    publicList: () => "/projects/public",
    publicCities: () => "/projects/public/cities",
    publicGetById: (id: string) => `/projects/public/${encodeURIComponent(id)}`,
    publicSimilar: (id: string) => `/projects/public/${encodeURIComponent(id)}/similar`,
    // ── STAFF (projects.* guarded admin tool) ─────────────────────────────────
    list: () => "/projects",
    getById: (id: string) => `/projects/${encodeURIComponent(id)}`,
    create: () => "/projects",
    update: (id: string) => `/projects/${encodeURIComponent(id)}`,
    remove: (id: string) => `/projects/${encodeURIComponent(id)}`,
    createUnit: (id: string) => `/projects/${encodeURIComponent(id)}/units`,
    updateUnit: (id: string, unitId: string) => `/projects/${encodeURIComponent(id)}/units/${encodeURIComponent(unitId)}`,
    removeUnit: (id: string, unitId: string) => `/projects/${encodeURIComponent(id)}/units/${encodeURIComponent(unitId)}`,
    // ── Off-plan inventory + holds (phase 2a) ─────────────────────────────────
    publicInventory: (id: string) => `/projects/public/${encodeURIComponent(id)}/inventory`,
    placeHold: (id: string, unitId: string) =>
      `/projects/public/${encodeURIComponent(id)}/inventory/${encodeURIComponent(unitId)}/hold`,
    inventory: (id: string) => `/projects/${encodeURIComponent(id)}/inventory`,
    inventoryUnit: (id: string, unitId: string) =>
      `/projects/${encodeURIComponent(id)}/inventory/${encodeURIComponent(unitId)}`,
    holds: (id: string) => `/projects/${encodeURIComponent(id)}/holds`,
    approveHold: (id: string, holdId: string) =>
      `/projects/${encodeURIComponent(id)}/holds/${encodeURIComponent(holdId)}/approve`,
    // ── Off-plan payment plan (phase 2b) ──────────────────────────────────────
    paymentPlan: (id: string) => `/projects/${encodeURIComponent(id)}/payment-plan`,
    publicPaymentPlan: (id: string) => `/projects/public/${encodeURIComponent(id)}/payment-plan`,
    rejectHold: (id: string, holdId: string) =>
      `/projects/${encodeURIComponent(id)}/holds/${encodeURIComponent(holdId)}/reject`,
  },
  tasks: {
    // Tasks CRUD (staff ops — guarded by `tasks.*`).
    list: () => "/tasks",
    getById: (id: string) => `/tasks/${encodeURIComponent(id)}`,
    create: () => "/tasks",
    update: (id: string) => `/tasks/${encodeURIComponent(id)}`,
    remove: (id: string) => `/tasks/${encodeURIComponent(id)}`,
    stats: (startDate?: string, endDate?: string) => {
      const params = new URLSearchParams();
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      const qs = params.toString();
      return qs ? `/tasks/stats?${qs}` : "/tasks/stats";
    },
    listingCounts: () => "/tasks/listing-counts",
    // Append-only task activity audit log (insert + list ONLY).
    activity: (taskId: string) => `/tasks/${encodeURIComponent(taskId)}/activity`,
    // Per-company payout cost per task category.
    typeCosts: () => "/tasks/type-costs",
    // Visits (staff ops — guarded by `tasks.*`).
    visits: () => "/tasks/visits",
    visit: (id: string) => `/tasks/visits/${encodeURIComponent(id)}`,
    // SELF read (S9): visits on the caller's OWN properties/units (auth-only,
    // no tasks.view — the server resolves the owner person-wide from the JWT).
    ownerVisits: () => "/tasks/visits/owner",
    // Track-E slice #3 flips: supplementary staff reads (guarded by `tasks.view`).
    byEmployee: (employeeId: string) => `/tasks/employee/${encodeURIComponent(employeeId)}`,
    // SELF read: the calling employee's own assigned tasks (auth-only, no
    // tasks.view — the server derives the employee from the JWT).
    mine: () => "/tasks/mine",
    recent: () => "/tasks/recent",
    details: (id: string) => `/tasks/${encodeURIComponent(id)}/details`,
    inspectionReport: (id: string) => `/tasks/${encodeURIComponent(id)}/inspection-report`,
    notes: (id: string) => `/tasks/${encodeURIComponent(id)}/notes`,
    byListing: (listingId: string) => `/tasks/listing/${encodeURIComponent(listingId)}/types`,
    // Assign the auto-created inspection task (POST — guarded by `tasks.edit`).
    assignInspection: () => "/tasks/assign-inspection",
    // PUBLIC, unauthenticated token lookup — see the public-token endpoint note.
    serviceRequestByToken: (token: string) => `/tasks/public/by-token/${encodeURIComponent(token)}`,
    submitVisitSchedule: () => "/tasks/public/submit-visit-schedule",
    // PUBLIC, unauthenticated lead capture from a listing page (no JWT, rate-limited).
    createLead: () => "/tasks/public/lead",
  },
  maintenance: {
    list: () => "/maintenance",
    getById: (id: string) => `/maintenance/${encodeURIComponent(id)}`,
    create: () => "/maintenance",
    update: (id: string) => `/maintenance/${encodeURIComponent(id)}`,
    remove: (id: string) => `/maintenance/${encodeURIComponent(id)}`,
    // Assignments (junction) are nested under their parent request.
    assignments: (requestId: string) => `/maintenance/${encodeURIComponent(requestId)}/assignments`,
    createAssignment: () => "/maintenance/assignments",
    updateAssignment: (id: string) => `/maintenance/assignments/${encodeURIComponent(id)}`,
    // Oracle-scoped staff read endpoints (no PermissionGuard).
    stats: () => "/maintenance/stats",
    reports: (startDate?: string, endDate?: string) => {
      const params = new URLSearchParams();
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      const qs = params.toString();
      return qs ? `/maintenance/reports?${qs}` : "/maintenance/reports";
    },
    staffList: (propertyId?: string, tenantId?: string) => {
      const params = new URLSearchParams();
      if (propertyId) params.set("property_id", propertyId);
      if (tenantId) params.set("tenant_id", tenantId);
      const qs = params.toString();
      return qs ? `/maintenance/staff?${qs}` : "/maintenance/staff";
    },
    // Owner/tenant portal reads (no PermissionGuard — self-service).
    ownerList: () => "/maintenance/owner",
    tenantList: (startDate?: string, endDate?: string) => {
      const params = new URLSearchParams();
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      const qs = params.toString();
      return qs ? `/maintenance/tenant?${qs}` : "/maintenance/tenant";
    },
    // Owner/tenant portal writes (self-service; server resolves the caller).
    tenantCreate: () => "/maintenance/tenant",
    tenantCancel: (id: string) => `/maintenance/tenant/${encodeURIComponent(id)}/cancel`,
    tenantPhotoUploadUrl: () => "/maintenance/tenant/photos/upload-url",
    ownerDecision: (id: string) => `/maintenance/owner/${encodeURIComponent(id)}/decision`,
    // Service-provider portal (self-service; server resolves provider identity
    // from the JWT — no provider/company id in the path).
    providerWorkOrders: () => "/maintenance/provider",
    providerAccept: (assignmentId: string) =>
      `/maintenance/provider/assignments/${encodeURIComponent(assignmentId)}/accept`,
    providerReject: (assignmentId: string) =>
      `/maintenance/provider/assignments/${encodeURIComponent(assignmentId)}/reject`,
    providerStart: (assignmentId: string) =>
      `/maintenance/provider/assignments/${encodeURIComponent(assignmentId)}/start`,
    providerComplete: (assignmentId: string) =>
      `/maintenance/provider/assignments/${encodeURIComponent(assignmentId)}/complete`,
    providerNote: (assignmentId: string) =>
      `/maintenance/provider/assignments/${encodeURIComponent(assignmentId)}/note`,
    providerQuote: (assignmentId: string) =>
      `/maintenance/provider/assignments/${encodeURIComponent(assignmentId)}/quote`,
  },
  brokerageContracts: {
    // The guarded Nest endpoint returns every brokerage contract for the
    // authenticated company and does NOT accept status/owner_id query params;
    // status- and owner-scoping is applied client-side in useBrokerageContracts
    // (a single per-company fetch + React Query `select`). See that hook.
    list: () => "/brokerage-contracts",
    getById: (id: string) => `/brokerage-contracts/${encodeURIComponent(id)}`,
    create: () => "/brokerage-contracts",
    update: (id: string) => `/brokerage-contracts/${encodeURIComponent(id)}`,
    remove: (id: string) => `/brokerage-contracts/${encodeURIComponent(id)}`,
    extract: () => "/brokerage-contracts/extract",
    document: (id: string) => `/brokerage-contracts/${encodeURIComponent(id)}/document`,
    links: (id: string) => `/brokerage-contracts/${encodeURIComponent(id)}/links`,
    unlink: (id: string, linkId: string) =>
      `/brokerage-contracts/${encodeURIComponent(id)}/links/${encodeURIComponent(linkId)}`,
    statusForProperty: (propertyId: string) =>
      `/brokerage-contracts/status/${encodeURIComponent(propertyId)}`,
    batchStatus: () => "/brokerage-contracts/status",
    // Unit-level standing: a unit's own unit-scoped contracts PLUS the
    // whole-property ones it inherits (a contract on a sibling unit does not
    // count). Server-side these reuse the property-keyed cache.
    statusForUnit: (unitId: string) =>
      `/brokerage-contracts/status/unit/${unitId}`,
    batchUnitStatus: () => "/brokerage-contracts/status/units",
  },
  // Leads (CRM demand surface) + nested interests. Prisma-first slice; the
  // guarded endpoints scope by JWT company. Interests are always nested under a
  // lead (`/leads/:leadId/interests`). Update is PATCH (the controllers use
  // @Patch — partial replacement), matching the Nest Update*Dto.
  leads: {
    list: () => "/leads",
    getById: (id: string) => `/leads/${encodeURIComponent(id)}`,
    // Lead-details lookup for the Task modal — the lead that spawned a given
    // kanban task (+ its interests). 404 when the task isn't lead-spawned.
    byTask: (taskId: string) => `/leads/by-task/${encodeURIComponent(taskId)}`,
    create: () => "/leads",
    update: (id: string) => `/leads/${encodeURIComponent(id)}`,
    remove: (id: string) => `/leads/${encodeURIComponent(id)}`,
    // AUTHED owner-portal self-service capture (Epic L / S6). Auth-only endpoint
    // (no service_requests.create permission) — owners hold the property_owner
    // role which lacks that slug; source is server-pinned to owner_portal.
    ownerInterest: () => "/leads/owner-interest",
    // AUTHED off-plan project booking from the owner portal (Epic L / project-booking).
    // Auth-only (SupabaseAuthGuard); source/userId/intents/use/city are server-pinned —
    // FE sends only project_id + identity + optional unit_type/message.
    ownerBooking: () => "/leads/owner-booking",
    // AUTHED anon→known merge (Epic L / S7). Post-login hook: folds anonymous
    // leads sharing the caller's verified phone onto their user. Auth-only — the
    // phone is read server-side from the caller's profile, never the body.
    merge: () => "/leads/merge",
    // AUTHED — ensure the signed-in user is in the Leads Address Book (Epic L:
    // every owner registration becomes a lead). Idempotently creates a
    // source=landing lead. Called on owner-portal mount (covers signup + login).
    self: () => "/leads/self",
    // AUTHED — the signed-in user's own leads + interests (owner "My Interests").
    mine: () => "/leads/mine",
    // AUTHED — leads tied to TASKS assigned to the calling employee (employee
    // portal "My Leads"). Auth-only; the employee is resolved server-side from
    // the JWT. Same row shape as `mine`.
    assigned: () => "/leads/assigned",
    // PUBLIC, UNAUTHENTICATED anonymous interest capture from an ad listing page
    // (Epic L / S5). companyId is derived server-side from the listing — never
    // sent. Rate-limited by IP (ThrottlerGuard). NO Authorization header.
    publicCapture: () => "/leads/public",
    // OTP for the public capture — accepts every supported-country mobile
    // (SA, AE, KW, BH, QA, OM, EG, JO), unlike the Saudi-only login
    // /auth/request-otp. Per-phone throttled. NO Authorization header.
    publicCaptureOtp: () => "/leads/public/request-otp",
    interests: {
      list: (leadId: string) => `/leads/${encodeURIComponent(leadId)}/interests`,
      getById: (leadId: string, id: string) =>
        `/leads/${encodeURIComponent(leadId)}/interests/${encodeURIComponent(id)}`,
      create: (leadId: string) => `/leads/${encodeURIComponent(leadId)}/interests`,
      update: (leadId: string, id: string) =>
        `/leads/${encodeURIComponent(leadId)}/interests/${encodeURIComponent(id)}`,
      remove: (leadId: string, id: string) =>
        `/leads/${encodeURIComponent(leadId)}/interests/${encodeURIComponent(id)}`,
    },
  },
  // Owner/tenant portal → company WhatsApp chat: the server sends an approved
  // template TO the caller's own verified phone (AUTHED, no body).
  portalChat: {
    startWhatsApp: () => "/portal-chat/whatsapp/start",
  },
  // Employee Referral Links — employee-facing surface, public /r/<code> resolve,
  // owner-facing attribution hooks, and admin oversight. Prisma-first slice.
  referrals: {
    // AUTHED employee: my personal link + collaborators (each with link) + counts.
    me: () => "/referrals/me",
    // AUTHED employee: owner registrations attributed to MY links (the funnel).
    myRegistrations: () => "/referrals/me/registrations",
    // AUTHED staff/admin: MY referral commission ledger.
    myCommissions: () => "/referrals/me/commissions",
    // AUTHED any user: who registered ME ("Registered by <employee>" on Profile).
    referredByMe: () => "/referrals/me/referred-by",
    // AUTHED employee: add / edit a collaborator; generate its referral link.
    collaborators: () => "/referrals/collaborators",
    updateCollaborator: (id: string) => `/referrals/collaborators/${encodeURIComponent(id)}`,
    collaboratorLink: (id: string) => `/referrals/collaborators/${encodeURIComponent(id)}/link`,
    // AUTHED employee: share a referral invite (SMS/WhatsApp/Email) to an owner.
    sendInvite: () => "/referrals/send-invite",
    // AUTHED owner post-signup hooks: record first-touch attribution / mark done.
    attribute: () => "/referrals/attribute",
    complete: () => "/referrals/complete",
    // PUBLIC — resolve a /r/<code> token (branding + click funnel). No auth.
    resolve: (code: string) => `/referrals/resolve/${encodeURIComponent(code)}`,
    // PUBLIC — pre-OTP "started" capture (owner entered phone, not yet verified).
    track: () => "/referrals/track",
    // AUTHED admin (employees.* permission): report + link/collaborator/reassign.
    admin: {
      report: () => "/referrals/admin/report",
      links: () => "/referrals/admin/links",
      // owners.view-gated: who registered this owner (the owner's record page).
      referredByOwner: (ownerId: string) =>
        `/referrals/admin/owners/${encodeURIComponent(ownerId)}/referred-by`,
      disableLink: (id: string) => `/referrals/admin/links/${encodeURIComponent(id)}/disable`,
      deactivateCollaborator: (id: string) =>
        `/referrals/admin/collaborators/${encodeURIComponent(id)}/deactivate`,
      reassign: (id: string) =>
        `/referrals/admin/registrations/${encodeURIComponent(id)}/reassign`,
      // employees.view read / finance.edit write: commission % per staff type.
      feeRates: () => "/referrals/admin/fee-rates",
      // finance.edit: accrue commissions now (the daily cron does the same).
      accrueCommissions: () => "/referrals/admin/commissions/accrue",
    },
  },
  serviceProviders: {
    list: () => "/service-providers",
    getById: (id: string) => `/service-providers/${encodeURIComponent(id)}`,
    create: () => "/service-providers",
    update: (id: string) => `/service-providers/${encodeURIComponent(id)}`,
    remove: (id: string) => `/service-providers/${encodeURIComponent(id)}`,
    // Provider self-service (portal) — GET + PUT; server resolves identity.
    me: () => "/service-providers/me",
  },
  // Elmam dynamic service catalog — public capture + staff/provider fulfillment.
  serviceCatalog: {
    offerings: () => "/service-catalog/offerings",
    capture: () => "/service-catalog/orders",
    orders: () => "/service-catalog/orders",
    providerOrders: () => "/service-catalog/provider/orders",
    providerAccept: (id: string) => `/service-catalog/provider/orders/${encodeURIComponent(id)}/accept`,
    providerComplete: (id: string) => `/service-catalog/provider/orders/${encodeURIComponent(id)}/complete`,
    // Super-admin catalog management (S14-A).
    createOffering: () => "/service-catalog/offerings",
    updateOffering: (id: string) => `/service-catalog/offerings/${encodeURIComponent(id)}`,
    deleteOffering: (id: string) => `/service-catalog/offerings/${encodeURIComponent(id)}`,
  },
  // Dynamic per-company fee-type settings (garage/parking/...).
  feeCategories: {
    list: () => "/fee-categories",
    create: () => "/fee-categories",
    update: (id: string) => `/fee-categories/${encodeURIComponent(id)}`,
    deactivate: (id: string) => `/fee-categories/${encodeURIComponent(id)}`,
  },
  employees: {
    // companyId is sent so a super_admin (whose JWT carries no single company)
    // stays scoped to the active company — without it the guarded endpoint's
    // scopeFor() returns null and lists employees across ALL companies.
    list: (includeInactive?: boolean, companyId?: string) => {
      const params = new URLSearchParams();
      if (includeInactive) params.set("includeInactive", "true");
      if (companyId) params.set("companyId", companyId);
      const qs = params.toString();
      return qs ? `/employees?${qs}` : "/employees";
    },
    // employees_with_status repoint — Track-E slice. Returns employees with
    // derived `status` field (verified | not_verified | not_invited) computed
    // by NestJS/Prisma, byte-identical to the Supabase view output.
    withStatus: (includeInactive?: boolean, companyId?: string) => {
      const params = new URLSearchParams();
      if (includeInactive) params.set("includeInactive", "true");
      if (companyId) params.set("companyId", companyId);
      const qs = params.toString();
      return qs ? `/employees/with-status?${qs}` : "/employees/with-status";
    },
    // Static path — must be listed before getById (/:id) to mirror the
    // controller's route declaration order (static before param).
    count: (companyId: string, activeOnly = true) =>
      `/employees/count?companyId=${encodeURIComponent(companyId)}&activeOnly=${activeOnly}`,
    getById: (id: string) => `/employees/${encodeURIComponent(id)}`,
    create: () => "/employees",
    update: (id: string) => `/employees/${encodeURIComponent(id)}`,
    remove: (id: string) => `/employees/${encodeURIComponent(id)}`,
    assignments: {
      list: (employeeId: string) => `/employees/${encodeURIComponent(employeeId)}/assignments`,
      assign: (employeeId: string) => `/employees/${encodeURIComponent(employeeId)}/assignments`,
      unassign: (employeeId: string) =>
        `/employees/${encodeURIComponent(employeeId)}/assignments/unassign`,
      clear: (employeeId: string) =>
        `/employees/${encodeURIComponent(employeeId)}/assignments`,
    },
    ratings: {
      list: (employeeId: string) => `/employees/${encodeURIComponent(employeeId)}/ratings`,
      listAll: (companyId: string) =>
        `/employees/ratings/all?companyId=${encodeURIComponent(companyId)}`,
      create: (employeeId: string) => `/employees/${encodeURIComponent(employeeId)}/ratings`,
    },
  },
  teams: {
    // Teams & 9-stage pipeline (Epic P). Guarded by employees.*.
    list: () => "/teams",
    stageOwnership: () => "/teams/stage-ownership",
    seedDefaults: () => "/teams/seed-defaults",
  },
  subscriptions: {
    // a13 slice. Guards mirror today's RLS: `me`/catalog reads are any
    // authenticated member; config writes, pricing-settings list and the
    // admin/* cross-tenant reads are super_admin (SuperAdminGuard).
    me: () => "/subscriptions/me",
    updateMyPlan: () => "/subscriptions/me/plan",
    plans: () => "/subscriptions/plans",
    pricingTiers: (planId?: string) =>
      planId
        ? `/subscriptions/pricing-tiers?planId=${encodeURIComponent(planId)}`
        : "/subscriptions/pricing-tiers",
    createPricingTier: () => "/subscriptions/pricing-tiers",
    pricingTier: (id: string) => `/subscriptions/pricing-tiers/${encodeURIComponent(id)}`,
    pricingSettings: () => "/subscriptions/pricing-settings",
    // The ONLY pricing-setting value readable by non-super-admins — the key is
    // allowlisted server-side (owner-portal "Request Service" catalog).
    serviceCatalogSetting: () => "/subscriptions/pricing-settings/service-catalog",
    pricingSetting: (key: string) => `/subscriptions/pricing-settings/${encodeURIComponent(key)}`,
    adminList: () => "/subscriptions/admin/list",
    adminStats: () => "/subscriptions/admin/stats",
  },
  // First-party analytics: the public tracker posts events; the super-admin
  // dashboard reads the summary (real super-admin JWT only).
  analytics: {
    events: () => "/analytics/events",
    summary: (params: { from: string; to: string; audience: "users" | "all" }) =>
      `/analytics/admin/summary?${new URLSearchParams(params).toString()}`,
  },
  qaRuns: {
    // Super-admin QA-runs viewer (Epic #2542, T5). The read side is guarded by
    // CompositeAuthGuard + SuperAdminGuard server-side; the ingest endpoints
    // (POST/PATCH) are ApiKeyGuard-only and are NOT consumed by the FE.
    list: (params?: { env?: string; status?: string; page?: number; limit?: number }) => {
      const qs = new URLSearchParams();
      if (params?.env) qs.set("env", params.env);
      if (params?.status) qs.set("status", params.status);
      if (params?.page) qs.set("page", String(params.page));
      if (params?.limit) qs.set("limit", String(params.limit));
      const s = qs.toString();
      return s ? `/qa-runs?${s}` : "/qa-runs";
    },
    getById: (id: string) => `/qa-runs/${encodeURIComponent(id)}`,
    artifactSignedUrl: (runId: string, artifactId: string) =>
      `/qa-runs/${encodeURIComponent(runId)}/artifacts/${encodeURIComponent(artifactId)}/url`,
    // Super-admin "Run QA now" — POST fires the qa-daily workflow on this env's
    // Nest (JWT super-admin only). Evidence ingests back to this env's store.
    trigger: () => "/qa-runs/trigger",
  },
  // Secured internal maintenance ops (conventions §11). Super-admin JWT or
  // X-API-Key; the FE hits them with the logged-in super-admin's JWT from the
  // hidden /super-admin/actions page. Backfills are idempotent + safe-only.
  ops: {
    visitRequestVisitsReport: () => "/ops/visit-request-visits-report",
    backfillVisitRequestVisits: () => "/ops/backfill-visit-request-visits",
    saleFeeLabelReport: () => "/ops/sale-fee-label-report",
    fixSaleBrokerageFeeInvoices: () => "/ops/fix-sale-brokerage-fee-invoices",
    nafathNamelessProfilesReport: () => "/ops/nafath-nameless-profiles-report",
    applyNafathNamelessProfilesFill: () =>
      "/ops/apply-nafath-nameless-profiles-fill",
    employeeNameSyncReport: () => "/ops/employee-name-sync-report",
    applyEmployeeNameSync: () => "/ops/employee-name-sync",
    saleInvoiceDescriptionReport: () => "/ops/sale-invoice-description-report",
    resyncSaleInvoiceDescriptions: () => "/ops/resync-sale-invoice-descriptions",
    nafathNamelessVerifiedReport: () => "/ops/nafath-nameless-verified-report",
    recoverNafathNamelessVerified: () =>
      "/ops/recover-nafath-nameless-verified",
  },
} as const;

export type ApiRoutes = typeof API_ROUTES;
